import { createStyles, Container, Text, Box, Title, Flex, Grid } from '@mantine/core';
import { AnimatedSlider } from './AnimatedSlider';
import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { hasSentLead, markLeadSent } from '../../utils/leadGuard';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    background: '#221e1d',
    paddingBottom: '60px',
    '@media (min-width: 992px)': {
      height: 'calc(100vh - 20px)',
      width: '100%',
      marginBottom: '0 !important',
      paddingBottom: '0'
    },
    '@media (max-width: 991px)': {
      minHeight: 'calc(100vh - var(--navbarHeight) - var(--footerHeight) + 60px)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      paddingBottom: '260px',
      marginBottom: '0 !important',
      overflow: 'hidden'
    }
  },
  title: {
    fontSize: '52px',
    lineHeight: '60px !important',
    letterSpacing: '-.02em',
    color: '#fff',
    margin: '10px 0 20px',
    fontWeight: '700',
    maxWidth: '600px',
    textAlign: 'left',
    // textTransform: 'uppercase',
    '@media (max-width: 1400px)': { fontSize: '42px', lineHeight: '50px' },
    '@media (max-width: 991px)': { fontSize: '32px', lineHeight: '38px !important' },
  },
  subtitle: {
    fontSize: '22px',
    lineHeight: '32px',
    letterSpacing: '-.01em',
    color: '#ccc',
    margin: '0 0 25px',
    fontWeight: '500',
    maxWidth: '560px',
    textAlign: 'left',
    '@media (max-width: 1400px)': { fontSize: '20px', lineHeight: '30px' },
    '@media (max-width: 991px)': { fontSize: '18px', lineHeight: '27px' },
  },
  testimonialsText: {
    fontSize: '22px',
    lineHeight: '24px',
    letterSpacing: '-.01em',
    color: '#ccc',
    margin: '0 0 25px',
    fontWeight: '500',
    maxWidth: '560px',
    '@media (max-width: 991px)': { fontSize: '17px', lineHeight: '24px' },
  },
  flexContainer: {
    padding: '0 60px',
    margin: '0 !important',
    marginTop: '-60px',
    textAlign: 'left',
    '@media (max-width: 991px)': {
      maxWidth: '100%',
      height: '100%',
      marginBottom: '30px',
      marginTop: '0',
      position: 'relative',
      zIndex: '5',
      padding: '10px 0 0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start !important'
    }
  },
  swiper: {
    '@media (max-width: 991px)': {
      position: 'relative',
      left: 0,
      right: 0,
      bottom: '-40px',
      width: '100%',
      zIndex: '1',
      opacity: '0.9',
      height: '230px',
      display: 'flex',
      alignItems: 'flex-end',
      pointerEvents: 'none'
    }
  },
  headerReview: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    '@media (max-width: 991px)': {
      alignItems: 'flex-start',
      paddingTop: '45px',
    }
  }
}));

const HeaderReview = () => {
  const { classes } = useStyles();

  const allTestimonials = [
    "/img/resultaten/april2024/resultaat1.jpg",
    "/img/resultaten/april2024/resultaat3.jpg",
    "/img/resultaten/april2024/resultaat5.jpg",
    "/img/resultaten/resultaat-21.jpg",
    "/img/resultaten/april2024/resultaat6.jpg",
    "/img/resultaten/april2024/resultaat7.jpg",
    "/img/resultaten/april2024/resultaat8.jpg",
    "/img/resultaten/april2024/resultaat9.jpg",
    "/img/resultaten/resultaat-30.jpg",
    "/img/resultaten/april2024/resultaat10.jpg",
    "/img/resultaten/april2024/resultaat4.jpg",
    "/img/resultaten/april2024/resultaat12.jpg"
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1-4: oude quiz, 5: formulier, 11: success na formulier + quiz uitnodiging, 7-10: quiz na uitnodiging, 6: final success
  const [quizData, setQuizData] = useState({
    doel: '',
    frustratie: '',
    ervaring: '',
    trainingsdagen: '',
    naam: '',
    email: '',
    telefoon: '',
    whatsappConsent: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [isDev, setIsDev] = useState(window.location.hostname.includes('localhost') || window.location.hostname.includes('ngrok'));
  useEffect(() => {
    setIsDev(window.location.hostname.includes('localhost') || window.location.hostname.includes('ngrok'));
  }, []);

  const [pageViewTracked, setPageViewTracked] = useState(false);
  useEffect(() => {
    if (!isDev && window.fbq && !pageViewTracked) {
      window.fbq('init', '336340599280167');
      window.fbq('track', 'PageView');
      setPageViewTracked(true);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => !isOpen && openPopup(), 10000);
    const handleExit = (e) => e.clientY <= 0 && !isOpen && openPopup();
    document.addEventListener('mouseleave', handleExit);
    return () => { clearTimeout(timer); document.removeEventListener('mouseleave', handleExit); };
  }, [isOpen]);

  // Voeg deze state toe bij je andere useState declarations
  // 50/50 split: als random nummer < 0.5, dan quiz, anders direct naar form
  const [variant, setVariant] = useState(() => {
    return Math.random() < 0.5 ? 'direct' : 'direct';
  });

  const openPopup = async () => {
    setIsOpen(true);
    
    // Als variant 'direct' is, ga direct naar stap 5
    if (variant === 'direct') {
      setStep(5);
    }
    
    if (!isDev && window.fbq) window.fbq('trackCustom', 'QuizStarted');
    if (!isDev) {
      try {
        await fetch('https://hook.eu2.make.com/gtmbqn4i2y6lq6dyut1x3bcl4tkyrwn6', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            event: 'quiz_opened', 
            page: 'header', 
            variant: variant, // Track welke variant getoond wordt
            timestamp: new Date().toISOString() 
          })
        });
      } catch (e) {}
    }
  };

  const nextStep = async (key, value) => {
    // Update quiz data eerst
    const updatedQuizData = { ...quizData, [key]: value };
    setQuizData(updatedQuizData);

    // Oude quiz flow (stappen 1-4) - alleen voor backwards compatibility
    if (step === 4 && !isDev) {
      try {
        await fetch('https://hook.eu2.make.com/gtmbqn4i2y6lq6dyut1x3bcl4tkyrwn6', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event: 'quiz_completed', page: 'header', step: step, variant: variant, timestamp: new Date().toISOString() })
        });
      } catch (e) {}
      setStep(step + 1);
      return;
    }

    // Nieuwe quiz flow na formulier (stap 10 = laatste quiz vraag)
    if (step === 10) {
      if (!isDev) {
        try {
          const formatPhoneNumber = (phone) => {
            if (!phone) return '';
            let digits = phone.replace(/\D/g, '');
            if (digits.startsWith('0') && digits.length >= 9) {
              return '+31' + digits.slice(1);
            }
            return phone;
          };

          const payload = {
            name: updatedQuizData.naam.trim(),
            email: updatedQuizData.email.trim(),
            phone: updatedQuizData.telefoon ? formatPhoneNumber(updatedQuizData.telefoon) : 'Geen',
            whatsappConsent: !!updatedQuizData.telefoon && updatedQuizData.whatsappConsent,
            doel: updatedQuizData.doel || 'Niet ingevuld',
            frustratie: updatedQuizData.frustratie || 'Niet ingevuld',
            ervaring: updatedQuizData.ervaring || 'Niet ingevuld',
            trainingsdagen: updatedQuizData.trainingsdagen || 'Niet ingevuld',
            variant: variant,
            source: 'header_persoonlijke_checklist_2025',
            timestamp: new Date().toISOString(),
            quizCompleted: true, // Markeer dat dit de tweede webhook is na quiz
          };

          await fetch('https://hook.eu2.make.com/7hmeffru455mqo2xtvcm18zwv8wal9yw', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } catch (e) {}
      }
      
      // Ga naar success scherm
      setStep(6);
      return;
    }

    // Voor alle andere stappen, ga gewoon naar de volgende stap
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!quizData.naam || !quizData.email) return setError('Vul je naam en e-mail in');

    setIsSubmitting(true);
    setError('');

    try {
      const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        let digits = phone.replace(/\D/g, '');
        if (digits.startsWith('0') && digits.length >= 9) {
          return '+31' + digits.slice(1);
        }
        return phone;
      };

      // Eerste webhook: alleen naam/email (na formulier, voor quiz)
      const firstPayload = {
        name: quizData.naam.trim(),
        email: quizData.email.trim(),
        phone: quizData.telefoon ? formatPhoneNumber(quizData.telefoon) : 'Geen',
        whatsappConsent: !!quizData.telefoon && quizData.whatsappConsent,
        doel: 'Niet ingevuld',
        frustratie: 'Niet ingevuld',
        ervaring: 'Niet ingevuld',
        trainingsdagen: 'Niet ingevuld',
        variant: variant,
        source: 'header_persoonlijke_checklist_2025',
        timestamp: new Date().toISOString(),
        quizCompleted: false, // Markeer dat dit de eerste webhook is (voor quiz)
      };

      if (!isDev) {
        await fetch('https://hook.eu2.make.com/7hmeffru455mqo2xtvcm18zwv8wal9yw', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(firstPayload),
        });
      }

      // Zelfde device maar één keer als Lead naar de Meta pixel
      if (!isDev && window.fbq && !hasSentLead('checklist')) {
        window.fbq('track', 'Lead');
        markLeadSent('checklist');
      }
      
      // Ga naar success scherm met quiz uitnodiging (stap 11)
      setStep(11);
    } catch (err) {
      setError('Er ging iets mis, probeer opnieuw');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* JOUW HERO – ALLEEN CTA TEKST GEWIJZIGD */}
      <Container className={classes.container} fluid>
        <Grid mt="0" className={classes.headerReview + " header-review"}>
          <Flex align="center" className={classes.flexContainer}>
            <Grid.Col span={12} md={7} style={{ maxWidth: '100vw' }}>
              <Title className={classes.title}>
                Ben je het zat om te trainen zonder goede resultaten?
              </Title>
              <Text className={classes.subtitle} style={{ margin: '20px 0' }}>
                Download nu gratis jouw trainingsplan naar gegarandeerde resultaten
              </Text>
              <Box my="xl">
                <div className={classes.testimonialsText} style={{ color: '#fff', fontSize: '14px' }}>
                  - Een duidelijk trainingsplan<br />
                  - Een voorbeeldschema dat je meteen kunt gebruiken<br />
                  - Direct toepasbaar
                </div>
              </Box>
              <button onClick={openPopup} className="button button-red" style={{ fontSize: '18px', padding: '12px 32px' }}>
                DOWNLOAD GRATIS PLAN →
              </button>
              <Text size="sm" color="#aaa" mt="md">
                100% gratis · Geen spam · Direct toepasbaar
              </Text>
            </Grid.Col>
            <Grid.Col sm={12} md={6} offset={{ md: 0.5 }} p="0" mr="lg">
              <Box className={classes.swiper}>
                <AnimatedSlider testimonials={allTestimonials} />
              </Box>
            </Grid.Col>
          </Flex>
        </Grid>
      </Container>

      {/* MULTI-STEP QUIZ POPUP */}
      {isOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto relative">
            <button onClick={() => setIsOpen(false)} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full z-10">
              <X size={24} />
            </button>

            {/* Stap 1 */}
            {step === 1 && (
              <div className="p-6 text-center">
                <h2 className="text-2xl font-bold mb-6 text-black">Wat is je hoofddoel?</h2>
                <div className="space-y-3">
                  <button onClick={() => nextStep('doel', 'Goed uitzien')} className="w-full text-sm py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">Ik wil er gewoon goed uitzien in de spiegel</button>
                  <button onClick={() => nextStep('doel', 'Kracht')} className="w-full text-sm py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">Ik wil sterker worden</button>
                </div>
                <p className="text-sm text-gray-500 mt-6">Vraag 1 van 5</p>
              </div>
            )}

            {/* Stap 2 – Jouw oude frustratievraag */}
            {step === 2 && (
              <div className="p-6">
                <h2 className="text-2xl font-bold text-center mb-6 text-black">Wat frustreert je het meest?</h2>
                <div className="space-y-3">
                  <button onClick={() => nextStep('frustratie', 'training')} className="w-full text-left p-4 border-2 border-gray-200 hover:border-red-500 rounded-xl transition">
                    <p className="font-bold">Ik train wel, maar zie geen verschil</p>
                    <p className="text-sm text-gray-600">Je schema klopt niet of je weet niet hoe je moet trainen</p>
                  </button>
                  <button onClick={() => nextStep('frustratie', 'voeding')} className="w-full text-left p-4 border-2 border-gray-200 hover:border-red-500 rounded-xl transition">
                    <p className="font-bold">Ik weet niet wat/hoeveel ik moet eten</p>
                    <p className="text-sm text-gray-600">Geen idee of je genoeg eet voor je doel</p>
                  </button>
                  <button onClick={() => nextStep('frustratie', 'beide')} className="w-full text-left p-4 border-2 border-red-500 bg-red-50 rounded-xl transition">
                    <p className="font-bold text-red-600">Ik heb gewoon geen duidelijk plan</p>
                    <p className="text-sm text-gray-700">Meest gekozen</p>
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-6 text-center">Vraag 2 van 5</p>
              </div>
            )}

            {/* Stap 3 */}
            {step === 3 && (
              <div className="p-6 text-center">
                <h2 className="text-2xl font-bold mb-6 text-black">Hoe lang train je al serieus?</h2>
                <div className="space-y-3">
                  <button onClick={() => nextStep('ervaring', 'Minder dan 1 jaar')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">Minder dan 1 jaar</button>
                  <button onClick={() => nextStep('ervaring', '1-3 jaar')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">1-3 jaar</button>
                  <button onClick={() => nextStep('ervaring', 'Meer dan 3 jaar')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">Meer dan 3 jaar</button>
                </div>
                <p className="text-sm text-gray-500 mt-6">Vraag 3 van 5</p>
              </div>
            )}

            {/* Stap 4 */}
            {step === 4 && (
              <div className="p-6 text-center">
                <h2 className="text-2xl font-bold mb-6 text-black">Hoe vaak train je per week?</h2>
                <div className="space-y-3">
                  <button onClick={() => nextStep('trainingsdagen', '1-2x')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">1-2x per week</button>
                  <button onClick={() => nextStep('trainingsdagen', '3-4x')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">3-4x per week</button>
                  <button onClick={() => nextStep('trainingsdagen', '5+')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">5+ keer per week</button>
                </div>
                <p className="text-sm text-gray-500 mt-6">Vraag 4 van 5</p>
              </div>
            )}

            {/* Stap 5 – Formulier met persoonlijke preview */}
            {step === 5 && (
              <div className="p-6 space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold mb-1 text-black">Bijna klaar!</h2>
                  <p className="text-lg">Vul je gegevens in en ontvang direct gratis:</p>

                  <ul className="text-sm text-gray-600 !list-disc !list-inside">
                    <li>- Een persoonlijk trainingsplan</li>
                    <li>- Een gratis voorbeeldschema</li>
                    <li>- Direct toegang</li>
                  </ul>
                  
                  {/* Toon alleen de situatie box als de quiz is ingevuld */}
                  {variant === 'quiz' && quizData.doel && (
                    <div className="bg-gray-100 p-4 rounded-lg text-left my-4 hidden">
                      <span>Jouw situatie:</span>
                      <p className="font-bold">{quizData.doel} + {quizData.frustratie === 'beide' ? 'training & voeding' : quizData.frustratie}</p>
                      <p className="text-sm text-gray-600">{quizData.ervaring} · {quizData.trainingsdagen} per week</p>
                    </div>
                  )}
                </div>

                {error && <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Voornaam *</label>
                    <input 
                      type="text" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" 
                      value={quizData.naam} 
                      onChange={e => setQuizData(prev => ({ ...prev, naam: e.target.value }))} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Waar mag ik jouw plan naartoe sturen? *</label>
                    <input 
                      type="email" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" 
                      value={quizData.email} 
                      onChange={e => setQuizData(prev => ({ ...prev, email: e.target.value }))} 
                      placeholder="jouw@email.com" 
                    />
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 hidden">
                    <p className="text-sm text-gray-700 mb-2">
                      Wil je het ook via WhatsApp ontvangen?
                    </p>
                    <p className="text-xs text-gray-600 mb-3">
                      Zo heb je direct toegang én kun je makkelijk vragen stellen
                    </p>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefoonnummer (niet verplicht)</label>
                    <input
                      type="tel"
                      placeholder="06 12345678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      value={quizData.telefoon}
                      onChange={e => setQuizData(prev => ({ ...prev, telefoon: e.target.value }))}
                    />
                    {quizData.telefoon && quizData.telefoon.trim() !== "" && (
                      <div className="flex items-start mt-3">
                        <input
                          type="checkbox"
                          className="mt-1 mr-2"
                          checked={quizData.whatsappConsent}
                          onChange={e => setQuizData(prev => ({ ...prev, whatsappConsent: e.target.checked }))}
                        />
                        <label className="text-xs text-gray-700">
                          Ja, stuur me het plan ook via WhatsApp
                        </label>
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 text-white font-semibold px-8 py-4 rounded-lg text-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Wordt verzonden...
                      </div>
                    ) : (
                      'STUUR MIJN PLAN'
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Stap 7 – Quiz vraag 1 (na formulier) */}
            {step === 7 && (
              <div className="p-6 text-center">
                <h2 className="text-2xl font-bold mb-6 text-black">Wat is je hoofddoel?</h2>
                <div className="space-y-3">
                  <button onClick={() => nextStep('doel', 'Goed uitzien')} className="w-full text-sm py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">Ik wil er gewoon goed uitzien in de spiegel</button>
                  <button onClick={() => nextStep('doel', 'Kracht')} className="w-full text-sm py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">Ik wil sterker worden</button>
                </div>
                <p className="text-sm text-gray-500 mt-6">Vraag 1 van 4</p>
              </div>
            )}

            {/* Stap 8 – Quiz vraag 2 (na formulier) */}
            {step === 8 && (
              <div className="p-6">
                <h2 className="text-2xl font-bold text-center mb-6 text-black">Wat frustreert je het meest?</h2>
                <div className="space-y-3">
                  <button onClick={() => nextStep('frustratie', 'training')} className="w-full text-left p-4 border-2 border-gray-200 hover:border-red-500 rounded-xl transition">
                    <p className="font-bold">Ik train wel, maar zie geen verschil</p>
                    <p className="text-sm text-gray-600">Je schema klopt niet of je weet niet hoe je moet trainen</p>
                  </button>
                  <button onClick={() => nextStep('frustratie', 'voeding')} className="w-full text-left p-4 border-2 border-gray-200 hover:border-red-500 rounded-xl transition">
                    <p className="font-bold">Ik weet niet wat/hoeveel ik moet eten</p>
                    <p className="text-sm text-gray-600">Geen idee of je genoeg eet voor je doel</p>
                  </button>
                  <button onClick={() => nextStep('frustratie', 'beide')} className="w-full text-left p-4 border-2 border-red-500 bg-red-50 rounded-xl transition">
                    <p className="font-bold text-red-600">Ik heb gewoon geen duidelijk plan</p>
                    <p className="text-sm text-gray-700">Meest gekozen</p>
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-6 text-center">Vraag 2 van 4</p>
              </div>
            )}

            {/* Stap 9 – Quiz vraag 3 (na formulier) */}
            {step === 9 && (
              <div className="p-6 text-center">
                <h2 className="text-2xl font-bold mb-6 text-black">Hoe lang train je al serieus?</h2>
                <div className="space-y-3">
                  <button onClick={() => nextStep('ervaring', 'Minder dan 1 jaar')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">Minder dan 1 jaar</button>
                  <button onClick={() => nextStep('ervaring', '1-3 jaar')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">1-3 jaar</button>
                  <button onClick={() => nextStep('ervaring', 'Meer dan 3 jaar')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">Meer dan 3 jaar</button>
                </div>
                <p className="text-sm text-gray-500 mt-6">Vraag 3 van 4</p>
              </div>
            )}

            {/* Stap 10 – Quiz vraag 4 (na formulier) */}
            {step === 10 && (
              <div className="p-6 text-center">
                <h2 className="text-2xl font-bold mb-6 text-black">Hoe vaak train je per week?</h2>
                <div className="space-y-3">
                  <button onClick={() => nextStep('trainingsdagen', '1-2x')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">1-2x per week</button>
                  <button onClick={() => nextStep('trainingsdagen', '3-4x')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">3-4x per week</button>
                  <button onClick={() => nextStep('trainingsdagen', '5+')} className="w-full py-4 bg-gray-50 hover:bg-red-50 border-2 border-transparent hover:border-red-500 rounded-xl transition">5+ keer per week</button>
                </div>
                <p className="text-sm text-gray-500 mt-6">Vraag 4 van 4</p>
              </div>
            )}

            {/* Stap 11 – Success na formulier + Quiz uitnodiging */}
            {step === 11 && (
              <div className="p-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-black">Check je inbox!</h3>
                <p className="text-gray-600 mb-6">Jouw persoonlijke plan is onderweg.</p>
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-gray-700 mb-4">
                    Om je persoonlijker advies te kunnen geven, kun je nog meer over jezelf vertellen.
                  </p>
                  <button 
                    onClick={() => setStep(7)} 
                    className="w-full bg-red-600 text-white font-semibold px-8 py-4 rounded-lg text-lg hover:bg-red-700 transition-colors"
                  >
                    Vertel meer over jezelf →
                  </button>
                  <button 
                    onClick={() => setIsOpen(false)} 
                    className="mt-3 text-gray-500 hover:text-gray-700 text-sm"
                  >
                    Later misschien
                  </button>
                </div>
              </div>
            )}

            {/* Success */}
            {step === 6 && (
              <div className="p-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-black">Check je inbox!</h3>
                <p className="text-gray-600">Jouw persoonlijke plan is onderweg.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HeaderReview;